import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { fetchCurrentUser } from "../services/meApi";
import type { CurrentUserResponse } from "../types/user";

export interface UseCurrentUserResult {
  isLoading: boolean;
  error: string | null;
  profile: CurrentUserResponse | null;
  isAdmin: boolean;
  refresh: () => Promise<void>;
}

export function useCurrentUser(): UseCurrentUserResult {
  const { isLoaded, isSignedIn } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<CurrentUserResponse | null>(null);

  const load = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setIsLoading(false);
      setProfile(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCurrentUser();
      setProfile(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load user profile";
      setError(message);
      setProfile({ is_admin: false });
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  return {
    isLoading,
    error,
    profile,
    isAdmin: profile?.is_admin === true,
    refresh,
  };
}
